import React, { useState, useEffect } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import Utils from 'Utils/utils';
import * as CONSTANTS from 'Utils/constants';
import crmIntegrationState from 'Stores/crmIntegration';
import userDetail from 'Stores/userDetail';
import { confirmationPopupState } from 'Components/common/confirmationPopup';
import LoadingWrapper from 'Components/common/loadingWrapper';
import SalesgearAPIConnect from 'Components/salesgear/salesgearAPIConnect';
import profileState from 'Stores/profile';
import enrichmentState, { enrichmentSteps } from 'Stores/enrichment';
import { commonUpgradePopupState } from 'Components/common/commonUpgradePopup';
import { IntegrationItemWrapper } from './integrationItemWrapper';
import integrationDataMap from './integrationDataMap';
import 'Styles/layout/_integration.scss';
import IntegrationPopup from './integrationPopup/integrationPopup';

const { UPGRADE_TRIGGERS } = CONSTANTS;
const Integration = observer(() => {
  const history = useHistory();
  const [showSGPopup, setShowSGPopup] = useState(false);
  const [integrationMap, setIntegrationMap] = useState({});
  const [integrationPopup, setIntegrationPopupData] = useState({
    show: false,
    data: {},
  });
  const { allowedCrms, crmConnectionStatus } = crmIntegrationState;

  useEffect(() => {
    crmIntegrationState.getCrmConnectionDetails();
  }, []);

  const successCallback = crmType => {
    history.push(
      `/integrations/${crmType.toLowerCase()}/field-mapping/contacts`,
    );
  };

  const connectToCrmClick = (e, crmType) => {
    if (e) e.preventDefault();

    if (!Utils.checkIsCRMExportEnabled()) {
      commonUpgradePopupState.setShowUpgradePopup(
        UPGRADE_TRIGGERS.CRM_INTEGRATIONS,
      );
    } else if (CONSTANTS.NEW_INTEGRATION_NOT_AVAILABLE_CRMS.includes(crmType)) {
      const crmData = integrationDataMap[crmType];
      setIntegrationPopupData({ show: true, data: crmData });
    } else if (crmType === 'SALESGEAR') {
      setShowSGPopup(true);
    } else {
      crmIntegrationState.connectToCrm({
        crmType,
        successCallback: () => successCallback(crmType),
      });
    }
  };

  const disconnectCrmClick = (e, crmType) => {
    if (e) e.preventDefault();

    const callback = async () => {
      if (crmType === 'SALESGEAR') {
        crmIntegrationState.isLoading = true;
        const isSuccess = await profileState.deleteSalesgearAPIKey();
        crmIntegrationState.isLoading = false;
        if (isSuccess)
          crmIntegrationState.setCrmConnectionStatus({ [crmType]: false });
      } else {
        // for showing enrichment steps again
        if (crmType === 'HUBSPOT')
          enrichmentState.setEnrichmentStepsCompleted(enrichmentSteps);
        crmIntegrationState.disconnectCrm(crmType);
      }
    };
    const isEnterpriseAdmin = userDetail.userInfo?.isEnterpriseAdmin;

    const crmName = integrationDataMap[crmType]?.title || crmType;

    confirmationPopupState.setPopupValues({
      customClassName: 'disconnect-crm',
      title: `Disconnecting from your ${crmName} CRM`,
      content:
        isEnterpriseAdmin &&
        !(crmType === 'SALESGEAR') &&
        userDetail.userInfo.noOfSeatsUsed > 1
          ? `As you disconnect, your team members will no longer be connected to your 
        ${crmName} CRM. Do you want to proceed?`
          : 'Are you sure?',
      actionBtnText: isEnterpriseAdmin ? 'Yes' : 'Proceed',
      callback,
    });
    confirmationPopupState.setShowPopup(true);
  };

  useEffect(() => {
    setIntegrationMap(integrationDataMap);
  }, []);

  return (
    <div className="integration-page">
      {integrationPopup.show && (
        <IntegrationPopup
          data={integrationPopup.data}
          closePopup={() => setIntegrationPopupData({ data: {}, show: false })}
        />
      )}
      {showSGPopup && (
        <SalesgearAPIConnect
          saveButtonText="Connect"
          closePopup={isSuccess => {
            if (isSuccess)
              crmIntegrationState.setCrmConnectionStatus({ SALESGEAR: true });
            setShowSGPopup(false);
          }}
        />
      )}

      <LoadingWrapper show={crmIntegrationState.isLoading} position="fixed" />
      <div className="page-title-text">Integrations</div>
      <div className="integration-content-wrapper">
        <div className="integration-list-wrapper">
          {allowedCrms.map((item, index) => {
            const dataMap = integrationMap[item] || null;
            if (dataMap)
              return (
                <IntegrationItemWrapper
                  key={`integration-item-${index}`}
                  dataMap={dataMap}
                  integrationType={item}
                  isConnected={crmConnectionStatus[item] || false}
                  isLoading={false}
                  connectCallback={connectToCrmClick}
                  disconnectCallback={disconnectCrmClick}
                />
              );

            return '';
          })}
        </div>
      </div>
    </div>
  );
});

export default Integration;
export { Integration };
